import { WEEKDAYS_SHORT, eventDays, eventName, termsWord, weekdayIndex, dayNumber, type CalendarEvent } from './calendar';
import { EVENT_STATUSES, formatEventRange, isClosed, settlementMarker } from './labels';
import { markStyle } from '../contractors/colors';

const MONTH_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

/**
 * Events as rows: the first day large on the left, the name (opens the details), the dates and place below it,
 * then the status and the "potrzebna wycena" flag. Used by the list view and by the phone's day lists.
 */
export function EventList({
  events,
  names,
  selectedId,
  onOpen,
  empty,
  label,
  compact = false,
  onNewOnDay,
}: {
  events: CalendarEvent[];
  names: Map<string, string>;
  selectedId: string | null;
  onOpen: (id: string) => void;
  empty: string;
  label?: string;
  compact?: boolean;
  /** P6: a double click on a row's date asks for a new event on that day. */
  onNewOnDay?: ((day: string) => void) | undefined;
}) {
  if (events.length === 0) return <p className="muted ev-empty">{empty}</p>;
  return (
    <ul className={`ev-list${compact ? ' compact' : ''}`} aria-label={label}>
      {events.map((e) => {
        // a role that may not see the dates gets "—" and no date block
        const first = e.startsAt && e.endsAt ? eventDays({ startsAt: e.startsAt, endsAt: e.endsAt }).first : null;
        const client = e.contractorId ? names.get(e.contractorId) : undefined;
        return (
          <li key={e.id} className={`ev-row${e.id === selectedId ? ' selected' : ''}${isClosed(e.status) ? ' cancelled' : ''}${e.clientColor ? ' client-mark' : ''}`} data-status={e.status} style={e.clientColor ? markStyle(e.clientColor) : undefined}>
            {!compact && (
              <div className="ev-date" aria-hidden="true" onDoubleClick={first && onNewOnDay ? () => onNewOnDay(first) : undefined}>
                {first && (
                  <>
                    <span className="ev-dow">{WEEKDAYS_SHORT[weekdayIndex(first)]}</span>
                    <span className="ev-daynum">{dayNumber(first)}</span>
                    <span className="ev-mon">{MONTH_SHORT[Number(first.slice(5, 7)) - 1]}</span>
                  </>
                )}
              </div>
            )}
            <div className="ev-main">
              <button type="button" className="ev-name" aria-label={`Szczegóły: ${eventName(e)}`} onClick={() => onOpen(e.id)}>
                {eventName(e)}
              </button>
              <div className="ev-meta">
                <span>{formatEventRange(e.startsAt, e.endsAt)}</span>
                {/* several terms (owner 2026-10-21 00:44): the range above, how many terms here */}
                {e.segments && e.segments.length > 1 && <span data-feature-id="events.segments">{`${e.segments.length} ${termsWord(e.segments.length)}`}</span>}
                {e.location && <span>{e.location}</span>}
                {client && <span>{client}</span>}
                {e.crew && e.crew.length > 0 && <span className="ev-crew" data-feature-id="crew.calendar">Obsada: {e.crew.join(', ')}</span>}
              </div>
            </div>
            <div className="ev-flags">
              {e.status && (
                <span className="status" data-status={e.status}>
                  {EVENT_STATUSES[e.status]}
                </span>
              )}
              {e.needsQuote && <span className="quote-flag">Potrzebna wycena</span>}
              {e.kind === 'wypozyczenie' && <span className="chip">Wypożyczenie</span>}
              {settlementMarker(e.settlement) && <span className="chip">{settlementMarker(e.settlement)}</span>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
